import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
} from "@tanstack/react-query";
import { getUserId, getUserShort } from "src/hooks/useUser";
import newApi from "src/newApi";
import { queryClient } from "src/queryClient";

import options from "./defaultOptions";

const key = "comments";

// initialComments: już pobrane komentarze (np. zbiorczo w adminie) dla domyślnych filtrów — bez requestu
export function useGetQuestionComments(id, filters, skip, initialComments) {
	const isDefaultFilters = filters.sort === "ASC" && !filters.type;

	return useQuery({
		queryKey: [key, id, filters],

		queryFn: async () => {
			return newApi.getQuestionComments(id, filters, skip);
		},

		...options,
		initialData: isDefaultFilters ? initialComments : undefined,
		enabled: !!skip,
		gcTime: 0,
		placeholderData: keepPreviousData,
	});
}

export function useGetComments(enabled) {
	return useInfiniteQuery({
		queryKey: ["comments"],
		queryFn: async ({ pageParam = 0 }) => {
			return newApi.getComments({
				page: pageParam,
				take: 10,
			});
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => {
			return lastPage.hasNextPage ? allPages.length : undefined;
		},
		select: (data) => {
			const flatComments = data.pages.flatMap((page) => page.comments);
			const comments = new Map();
			flatComments.forEach((comment) => {
				comments.set(comment.id, comment);
			});

			return {
				pages: Array.from(comments.values()),
				pageParams: data.pageParams,
				currentPage: data.pages.length,
			};
		},
		...options,
		staleTime: 0,
		gcTime: 0,
		placeholderData: keepPreviousData,
		enabled,
	});
}

export function useGetForumComments(params = null, enabled) {
	return useInfiniteQuery({
		queryKey: ["forumComments", params],
		queryFn: async ({ pageParam = 0 }) => {
			return newApi.getForumComments({
				page: pageParam,
				take: 10,
				...(params?.search ? { search: params.search } : {}),
			});
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage, allPages) => {
			return (allPages.length === 1 && lastPage?.comments.length > 2) ||
				lastPage.hasNextPage
				? allPages.length
				: undefined;
		},
		select: (data) => {
			const flatComments = data.pages.flatMap((page) => page.comments);
			const comments = new Map();
			flatComments.forEach((comment) => {
				comments.set(comment.id, comment);
			});

			return {
				pages: Array.from(comments.values()),
				pageParams: data.pageParams,
				currentPage: data.pages.length,
			};
		},
		...options,
		staleTime: 0,
		gcTime: 0,
		placeholderData: keepPreviousData,
		enabled,
	});
}

export function useGetForumCommentSubcomments(parentId, enabled) {
	return useQuery({
		queryKey: ["forumComments", parentId],

		queryFn: async () => {
			return newApi.getForumCommentSubcomments(parentId);
		},

		...options,
		enabled,
	});
}

const toggleLike = (likes, commentId) => {
	if (likes.some(({ userId }) => userId === getUserId())) {
		return likes.filter(({ userId }) => userId !== getUserId());
	} else {
		return [
			...likes,
			{
				id: 0,
				commentId,
				userId: getUserId(),
			},
		];
	}
};

export function useToggleForumCommentLike() {
	return useMutation({
		mutationFn: ({ id }) => {
			return newApi.toggleCommentLike(id);
		},
		onSuccess: async (_data, params) => {
			if (params.parentId) {
				queryClient.setQueryData(
					["forumComments", params.parentId],
					(comment) => {
						return {
							...comment,
							subComments: comment.subComments.map((subComment) => {
								if (subComment.id === params.id) {
									return {
										...subComment,
										likes: toggleLike(subComment.likes, subComment.id),
									};
								}

								return subComment;
							}),
						};
					},
				);
			}
		},
	});
}

export function useAddForumComment() {
	return useMutation({
		mutationFn: async ({ id, comment, parentId, receiverId, image }) => {
			let imgDetails = null;
			if (image) {
				const url = (await newApi.uploadImage(image.image.file)).url;
				imgDetails = {
					url,
					...(image.source.id
						? { sourceId: image.source.id }
						: { source: image.source.source }),
				};
			}

			const res = await newApi.addForumComment(id, {
				comment,
				parentId,
				receiverId,
				image: imgDetails,
			});

			return {
				...res,
				imgDetails,
			};
		},
		onSuccess: async (data, params) => {
			await queryClient.refetchQueries({ queryKey: ["forum-comments-count"] });
			const existingCommentsData = queryClient.getQueryData([
				"forumComments",
				params.options,
			]);
			if (existingCommentsData && !params.parentId) {
				queryClient.setQueryData(["forumComments", params.options], (u) => {
					const [firstPage, ...pages] = u.pages;
					const newComment = {
						id: data.id,
						comment: params.comment,
						data: new Date().toISOString(),
						userId: getUserId(),
						images: data.imgDetails
							? [
									{
										url: data.imgDetails.url,
									},
								]
							: [],
						user: getUserShort(),
						subcommentsCount: 0,
					};
					return {
						...u,
						pages: [
							{ ...firstPage, comments: [newComment, ...firstPage.comments] },
							...pages,
						],
					};
				});
			}

			if (params.parentId) {
				await queryClient.refetchQueries({
					queryKey: ["forumComments", params.parentId],
				});
			}
		},
	});
}

export function useAddComment() {
	return useMutation({
		mutationFn: async ({ id, comment, parentId, receiverId, image }) => {
			let imgDetails = null;
			if (image) {
				const { url } = await newApi.uploadImage(image.image.file);
				imgDetails = {
					url,
					...(image.source.id
						? { sourceId: image.source.id }
						: { source: image.source.source }),
				};
			}

			const res = await newApi.addComment(id, {
				comment,
				parentId,
				receiverId,
				image: imgDetails,
			});

			return {
				...res,
				imgDetails,
			};
		},
		onSuccess: async (data, params) => {
			const existingCommentsData = queryClient.getQueryData(["comments"]);
			if (existingCommentsData) {
				queryClient.setQueryData(["comments"], (u) => {
					const [firstPage, ...pages] = u.pages;
					const newComment = {
						id: 123123,
						comment: params.comment,
						data: new Date().toISOString(),
						userId: getUserId(),
						questionId: params.question.id,
						images: data.imgDetails
							? [
									{
										url: data.imgDetails.url,
									},
								]
							: [],
						question: {
							question: params.question.pytanie,
							terms: params.question.termin.split(",").map((item) => {
								return {
									termShort: item,
								};
							}),
						},
						user: getUserShort(),
					};
					return {
						...u,
						pages: [
							{ ...firstPage, comments: [newComment, ...firstPage.comments] },
							...pages,
						],
					};
				});
			}
			await Promise.all([
				queryClient.refetchQueries({ queryKey: [key, params.id] }),
				queryClient.refetchQueries({ queryKey: ["exam-question", params.id] }),
				queryClient.refetchQueries({ queryKey: ["exam-question", params.id] }),
				queryClient.invalidateQueries({ queryKey: ["questions"] }),
			]);
		},
	});
}

export function useRemoveComment() {
	return useMutation({
		mutationFn: ({ id }) => {
			return newApi.deleteComment(id);
		},
		onSuccess: async (_data, params) => {
			await Promise.all([
				queryClient.refetchQueries({ queryKey: [key, params.questionId] }),
				queryClient.refetchQueries({
					queryKey: ["exam-question", params.questionId],
				}),
				queryClient.refetchQueries({
					queryKey: ["exam-question", params.questionId],
				}),
				queryClient.invalidateQueries({ queryKey: ["questions"] }),
			]);
		},
	});
}

export function useRemoveForumComment() {
	return useMutation({
		mutationFn: ({ id }) => {
			return newApi.deleteComment(id);
		},
		onSuccess: async (_data, params) => {
			await queryClient.refetchQueries({ queryKey: ["forum-comments-count"] });
			if (params.parentId) {
				queryClient.setQueryData(
					["forumComments", params.parentId],
					(comment) => {
						return {
							...comment,
							subComments: comment.subComments.filter(
								(subComment) => subComment.id !== params.id,
							),
						};
					},
				);
			} else {
				const allQueries = queryClient.getQueriesData({
					queryKey: ["forumComments"],
				});

				allQueries.forEach(([queryKey, oldData]) => {
					if (!oldData || !oldData.pages) {
						return;
					}

					const newPages = oldData.pages.map((page) => {
						return {
							...page,
							comments: page.comments.filter(
								(comment) => comment.id !== params.id,
							),
						};
					});

					queryClient.setQueryData(queryKey, {
						...oldData,
						pages: newPages,
					});
				});
			}
		},
	});
}

export function useToggleCommentLike() {
	return useMutation({
		mutationFn: ({ id }) => {
			return newApi.toggleCommentLike(id);
		},
		onSuccess: async (_data, params) => {
			await Promise.all([
				queryClient.refetchQueries({ queryKey: [key, params.questionId] }),
			]);
		},
	});
}

export function useSources() {
	const specId = newApi.getUserSpecialization();
	return useQuery({
		queryKey: [`sources-${specId}`],

		queryFn: async () => {
			return newApi.getSources();
		},

		...options,
		staleTime: 0,
	});
}

export function useForumCommentCount() {
	return useQuery({
		queryKey: [`forum-comments-count`],

		queryFn: async () => {
			return newApi.getForumCommentsCount();
		},

		...options,
		staleTime: 0,
	});
}
